import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Compass, Bookmark, BookmarkCheck, Play, ShieldCheck, Sparkles, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PageHeader, Card, EmptyState, Field, Tag, ErrorBox, Spinner } from "@/components/lf";
import FollowUp from "@/components/FollowUp";
import { useStore, useAsync, fmtDate } from "@/lib/hooks";
import { add, write, update, read, uid, StorageError } from "@/lib/storage";
import { fetchRecommendations, aiStatus } from "@/lib/api";
import { matchCatalog } from "@/lib/catalog";
import { startReading } from "@/lib/stats";

const List = ({ title, items }) =>
  items && items.length > 0 ? (
    <div className="mb-5">
      <div className="lf-overline mb-2">{title}</div>
      <ul className="space-y-1.5 text-sm text-[#F2F0EC] leading-relaxed list-disc pl-5 marker:text-[#545B6B]">{items.map((x, i) => <li key={i}>{x}</li>)}</ul>
    </div>
  ) : null;

const Provenance = ({ entry }) =>
  entry.catalogId ? (
    <Tag tone="ivory" data-testid="badge-catalog"><ShieldCheck size={11} className="mr-1" />Terverifikasi — ada di katalog Toko Buku</Tag>
  ) : (
    <Tag tone="warn" data-testid="badge-ai"><Sparkles size={11} className="mr-1" />Saran AI — verifikasi judul sebelum membeli</Tag>
  );

function DetailDialog({ entry, onClose }) {
  if (!entry) return null;
  const b = entry.book;
  const ctx = `Buku: ${b.judul} oleh ${b.penulis}. Pertanyaan pembaca: ${entry.question}. Alasan: ${b.alasan_relevansi}. Konsep: ${b.konsep_terkait.join(", ")}.`;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="reco-detail-dialog" className="bg-[#1B1B1F] border-[#29292D] text-[#F2F0EC] max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl font-medium">{b.judul}</DialogTitle>
          <DialogDescription className="text-[#929197]">{b.penulis}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2"><Provenance entry={entry} /><Tag>Keyakinan AI: {b.keyakinan}</Tag></div>
        <div className="mb-1"><div className="lf-overline mb-2">Mengapa relevan</div><p className="text-sm leading-relaxed">{b.alasan_relevansi}</p></div>
        <List title="Konsep filosofis terkait" items={b.konsep_terkait} />
        <List title="Hasil belajar yang diharapkan" items={b.hasil_belajar} />
        {b.bagian_awal && <div className="mb-5"><div className="lf-overline mb-2">Bagian awal yang disarankan</div><p className="text-sm">{b.bagian_awal}</p></div>}
        {b.urutan_baca && <div className="mb-5"><div className="lf-overline mb-2">Urutan baca</div><p className="text-sm">{b.urutan_baca}</p></div>}
        <List title="Latihan praktis" items={b.latihan_praktis} />
        <List title="Pertanyaan lanjutan" items={b.pertanyaan_lanjutan} />
        {!entry.catalogId && <p className="text-xs text-[#d4a95f] flex gap-2"><AlertTriangle size={14} className="shrink-0" />Judul ini disarankan AI dan belum diverifikasi terhadap katalog. Periksa keberadaan buku sebelum membeli.</p>}
        <FollowUp type="rekomendasi" contextText={ctx} />
      </DialogContent>
    </Dialog>
  );
}

function RecoCard({ entry, onDetail, onToggleSave, onStart }) {
  const b = entry.book;
  return (
    <div className="lf-card lf-card-hover p-6 flex flex-col" data-testid="reco-card">
      <div className="mb-3"><Provenance entry={entry} /></div>
      <h3 className="font-serif text-3xl leading-tight font-medium" data-testid="reco-card-title">{b.judul}</h3>
      <div className="text-sm text-[#929197] mb-3">{b.penulis}</div>
      <p className="text-sm text-[#F2F0EC]/85 leading-relaxed line-clamp-4 flex-1">{b.alasan_relevansi}</p>
      <div className="flex flex-wrap gap-1.5 mt-3">{b.konsep_terkait.slice(0, 4).map((c) => <Tag key={c} tone="blue">{c}</Tag>)}</div>
      <div className="flex flex-wrap gap-2 mt-5">
        <button data-testid="reco-detail-button" onClick={onDetail} className="lf-btn !py-2">Lihat detail</button>
        <button data-testid="reco-save-button" onClick={onToggleSave} className="lf-btn-ghost !py-2">{entry.saved ? <><BookmarkCheck size={14} />Tersimpan</> : <><Bookmark size={14} />Simpan</>}</button>
        <button data-testid="reco-start-button" onClick={onStart} className="lf-btn-ghost !py-2"><Play size={14} />Mulai membaca</button>
      </div>
    </div>
  );
}

export default function KompasBuku() {
  const loc = useLocation();
  const navigate = useNavigate();
  const settings = useStore("settings");
  const recs = useStore("recommendations");
  const [form, setForm] = useState({ question: loc.state?.question || "", goal: "", level: settings.preferredLevel, intensity: settings.preferredIntensity });
  const [status, setStatus] = useState(null);
  const [detail, setDetail] = useState(null);
  const [st, run] = useAsync();

  useEffect(() => { aiStatus().then(setStatus); }, []);

  const latestBatch = recs.length ? recs[0].batchId : null;
  const latest = recs.filter((r) => r.batchId === latestBatch);
  const saved = recs.filter((r) => r.saved);
  const meta = latest[0]?.meta;

  const submit = async (e) => {
    e.preventDefault();
    if (form.question.trim().length < 5) return toast.error("Tuliskan pertanyaan atau masalah Anda (minimal 5 karakter).");
    const res = await run(() => fetchRecommendations({ ...form, question: form.question.trim(), goal: form.goal.trim() }));
    if (!res) return;
    try {
      const keep = read("recommendations").filter((r) => r.saved);
      write("recommendations", keep);
      const batchId = uid("batch");
      res.rekomendasi.slice().reverse().forEach((b) => {
        const cat = matchCatalog(b.judul);
        add("recommendations", {
          batchId, question: form.question.trim(), goal: form.goal.trim(), level: form.level, intensity: form.intensity,
          saved: false, catalogId: cat ? cat.id : null, book: b,
          meta: { ringkasan: res.ringkasan_pertanyaan, urutan: res.urutan_membaca, catatan: res.catatan, model: res.model },
        });
      });
      toast.success("Rekomendasi siap.");
    } catch (err) {
      toast.error(err instanceof StorageError ? err.message : "Gagal menyimpan hasil.");
    }
  };

  const toggleSave = (entry) => {
    try { update("recommendations", entry.id, { saved: !entry.saved }); toast.success(entry.saved ? "Dihapus dari simpanan." : "Rekomendasi disimpan."); }
    catch (err) { toast.error(err.message); }
  };

  const start = (entry) => {
    const cat = entry.catalogId ? matchCatalog(entry.book.judul) : null;
    try {
      const { created } = startReading({ title: cat ? cat.title : entry.book.judul, author: cat ? cat.author : entry.book.penulis, totalPages: cat ? cat.pages : 0, source: "ai", catalogId: cat?.id });
      toast.success(created ? "Ditambahkan ke Progres Membaca." : "Buku ini sudah ada di Progres Membaca.");
      navigate("/progres-membaca");
    } catch (err) { toast.error(err.message); }
  };

  const grid = (items, testid, emptyEl) =>
    items.length === 0 ? emptyEl : (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid={testid}>
        {items.map((e) => <RecoCard key={e.id} entry={e} onDetail={() => setDetail(e)} onToggleSave={() => toggleSave(e)} onStart={() => start(e)} />)}
      </div>
    );

  const unconfigured = status && !status.configured;

  return (
    <div>
      <PageHeader testid="page-title-kompas" overline="Ruang kerja" title="Kompas Buku" subtitle="Ceritakan pertanyaan atau masalah Anda. Kami akan menyarankan buku, urutan baca, dan latihan yang sesuai tingkat Anda." />

      {unconfigured && (
        <div data-testid="ai-config-required" className="border border-[#7a5c2e] bg-[#1c1710] text-[#d4a95f] text-sm rounded-sm px-4 py-3 mb-6">
          Konfigurasi diperlukan: layanan AI belum dikonfigurasi di server, sehingga rekomendasi tidak dapat dibuat.
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-8">
        <form onSubmit={submit} className="xl:col-span-2 lf-card p-6 space-y-5 h-fit" data-testid="reco-form">
          <Field label="Pertanyaan atau masalah Anda" hint={`${form.question.length}/1200`}>
            <textarea data-testid="reco-question-input" rows={5} maxLength={1200} value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} placeholder="Mis. Bagaimana cara tetap tenang menghadapi hal yang tidak bisa saya kendalikan?" className="lf-field" />
          </Field>
          <Field label="Tujuan membaca"><input data-testid="reco-goal-input" maxLength={600} value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} placeholder="Mis. membangun kebiasaan refleksi harian" className="lf-field" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tingkat pengetahuan"><select data-testid="reco-level-select" className="lf-field" value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })}>{["Pemula", "Menengah", "Lanjutan"].map((x) => <option key={x}>{x}</option>)}</select></Field>
            <Field label="Intensitas membaca"><select data-testid="reco-intensity-select" className="lf-field" value={form.intensity} onChange={(e) => setForm({ ...form, intensity: e.target.value })}>{["Ringan", "Sedang", "Mendalam"].map((x) => <option key={x}>{x}</option>)}</select></Field>
          </div>
          <button data-testid="reco-submit-button" disabled={st.loading || unconfigured} className="lf-btn w-full !py-3">{st.loading ? <Spinner label="Kami akan menyusun rekomendasi…" /> : <><Compass size={16} />Dapatkan rekomendasi</>}</button>
          <ErrorBox error={st.error} testid="reco-error" />
        </form>

        <div className="xl:col-span-3">
          <Tabs defaultValue="terbaru">
            <TabsList className="bg-transparent border-b border-[#29292D] rounded-none w-full justify-start gap-6 p-0 h-auto mb-6">
              <TabsTrigger data-testid="tab-latest" value="terbaru" className="rounded-none px-0 pb-3 bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-[#F2F0EC] data-[state=active]:border-b-2 data-[state=active]:border-[#E6E2D6] text-[#929197]">Hasil terbaru ({latest.length})</TabsTrigger>
              <TabsTrigger data-testid="tab-saved" value="tersimpan" className="rounded-none px-0 pb-3 bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-[#F2F0EC] data-[state=active]:border-b-2 data-[state=active]:border-[#E6E2D6] text-[#929197]">Tersimpan ({saved.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="terbaru">
              {meta && (
                <div className="mb-5" data-testid="reco-summary">
                  {meta.ringkasan && <p className="font-serif text-xl italic text-[#F2F0EC]/90 mb-3">“{meta.ringkasan}”</p>}
                  {meta.urutan?.length > 0 && <p className="text-sm text-[#929197]"><span className="text-[#F2F0EC]">Urutan membaca:</span> {meta.urutan.join(" → ")}</p>}
                  <p className="text-xs text-[#6b6a70] mt-1">Dibuat {fmtDate(latest[0].createdAt)} · Model {meta.model}</p>
                </div>
              )}
              {grid(latest, "reco-results", <EmptyState testid="empty-reco" title="Belum ada rekomendasi" text="Isi formulir di samping untuk mendapatkan saran buku dari AI." />)}
            </TabsContent>
            <TabsContent value="tersimpan">
              {grid(saved, "reco-saved", <EmptyState testid="empty-saved" title="Belum ada yang disimpan" text="Tekan “Simpan” pada rekomendasi untuk menyimpannya di sini." />)}
            </TabsContent>
          </Tabs>
        </div>
      </div>
      <DetailDialog entry={detail} onClose={() => setDetail(null)} />
    </div>
  );
}
